import { isTeller } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Teller V2 — pooled fixed-term, fixed-APR loans with TIME-based default.
 *
 * The most dangerous market we serve, and the reason the disclosure gate
 * exists at all:
 *  - There is **no oracle and no health factor** anywhere in the trigger.
 *    `oracle.kind: 'none'` is a FACT here, not missing data.
 *  - On default `liquidateLoanFull` seizes the **entire** escrowed collateral,
 *    not just the amount owed — roughly 2x the borrowed value at 50 % LTV.
 *  - The grace period is a live market parameter observed as low as **300 s**.
 *
 * A UI showing a green health bar for a Teller loan is actively misleading,
 * which is why `liquidation.trigger: 'time'` and `seizure: 'full-collateral'`
 * both map to `critical` severity.
 */
export const tellerAdapter: TermAdapter = {
  id: 'teller',
  matches: isTeller,
  profileId: () => 'teller.pool@v1',
  build: (input) => {
    const teller = (input.market?.teller ?? {}) as Record<string, unknown>
    const graceSecs =
      typeof teller.paymentDefaultDuration === 'number'
        ? teller.paymentDefaultDuration
        : undefined
    const attested = teller.requiresBorrowerAttestation === true

    return {
      // Not a coverage hole: Teller genuinely has no price input.
      oracle: { kind: 'none' },
      supply: {
        exit: { mode: 'queued', settlement: 'async', partialAllowed: true },
        counterparty: { kind: 'pool', solvency: 'undercollateralized' },
        principal: { protected: false, risks: ['bad-debt'] },
      },
      borrow: {
        debtShape: 'accruing',
        exit: {
          // Free, pro-rata early repayment — but only a FULL close is
          // supported; there is no partial collateral withdrawal.
          earlyRepay: 'free',
          atMaturityCost: 'accrued',
          lateBehaviour: 'default-seizure',
          partialAllowed: false,
        },
        maturity: { atMaturity: 'default-seizure', graceSecs },
        liquidation: {
          model: 'default-seizure',
          absorber: 'liquidator',
          trigger: 'time',
          seizure: 'full-collateral',
          gracePeriodSecs: graceSecs,
          windowSecs: graceSecs,
          badDebt: 'socialized',
        },
        counterparty: { kind: 'pool', solvency: 'undercollateralized' },
        availability: attested
          ? { gating: 'attestation', requires: ['token-approval'] }
          : undefined,
      },
      constraints: {
        positionModel: 'loan-id',
        positionIdMeaning: 'The Teller bidId of this loan.',
      },
    }
  },
}
